/**
 * SBI dashboard – Performance Overview with gauge metrics (demo design).
 * Supports dark/light theme via header switcher; theme stored in context.
 */
import { useState } from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import WeekDateFilter from "../core/components/WeekDateFilter";
import KpiDashboard from "./components/KpiDashboard";
import SbiHeader from "../../components/SbiHeader";
import { useMaterialUIController } from "context";
import { SBI_DARK, SBI_LIGHT } from "assets/theme/base/colors";
import { useBranding, FRESENIUS } from "contextBranding";

function SbiDark() {
  const [controller] = useMaterialUIController();
  const { sbiDashboardTheme } = controller;
  const { isFreseniusBranding } = useBranding();
  const [dateFilter, setDateFilter] = useState({ type: "all" });
  const [benchmarkMode, setBenchmarkMode] = useState("overall");

  const isLight = sbiDashboardTheme === "light";
  const theme = isLight ? SBI_LIGHT : SBI_DARK;
  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const contentBg = isFreseniusBranding ? freseniusTheme.bgMain : theme.bgMain;
  const dashboardContentBg =
    isFreseniusBranding && isLight
      ? "#ffffff"
      : isFreseniusBranding && !isLight
        ? "#002a52" /* darker blue for dashboard background; panels use header blue */
        : contentBg;

  const handleBenchmarkMode = (_, value) => {
    if (value != null) setBenchmarkMode(value);
  };

  const headerLinkColor = isFreseniusBranding ? freseniusTheme.link : theme.link;
  const headerBorderColor = isFreseniusBranding ? freseniusTheme.border : theme.border;
  const headerTextPrimary = isFreseniusBranding ? freseniusTheme.textPrimary : theme.textPrimary;
  const headerRightContent = (
    <>
      <MDBox display="flex" alignItems="center" gap={1}>
        <MDTypography variant="caption" sx={{ color: headerLinkColor, fontWeight: 500 }}>
          Benchmark:
        </MDTypography>
        <ToggleButtonGroup
          value={benchmarkMode}
          exclusive
          onChange={handleBenchmarkMode}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              py: 0.5,
              px: 1.5,
              fontSize: "0.75rem",
              color: headerLinkColor,
              borderColor: headerBorderColor,
              "&.Mui-selected": {
                backgroundColor: isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)",
                color: headerTextPrimary,
                "&:hover": {
                  backgroundColor: isLight ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.18)",
                },
              },
            },
          }}
        >
          <ToggleButton value="overall" aria-label="Benchmark: Overall">
            Overall
          </ToggleButton>
          <ToggleButton value="regional" aria-label="Benchmark: Regional">
            Regional
          </ToggleButton>
        </ToggleButtonGroup>
      </MDBox>
      <WeekDateFilter
        value={dateFilter}
        onChange={setDateFilter}
        buttonBg={isFreseniusBranding ? (isLight ? FRESENIUS.blue : FRESENIUS.blueLight) : (isLight ? "#1f4e50" : "#256b5c")}
      />
    </>
  );

  return (
    <DashboardLayout>
      <SbiHeader rightContent={headerRightContent} />

      <MDBox
        pt={3}
        pb={3}
        px={{ xs: 2, md: 3 }}
        sx={{
          backgroundColor: dashboardContentBg,
          color: isFreseniusBranding ? freseniusTheme.textPrimary : theme.textPrimary,
          height: "calc(100vh - 72px)",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <KpiDashboard
          dateFilter={dateFilter}
          theme={sbiDashboardTheme}
          benchmarkMode={benchmarkMode}
          panelBg={
            isFreseniusBranding
              ? (isLight ? FRESENIUS.themeLight.bgHeader : FRESENIUS.themeDark.bgHeader)
              : undefined
          }
        />
      </MDBox>
    </DashboardLayout>
  );
}

export default SbiDark;
